
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BookOpen, Settings, BookMarked } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CategoryList } from "@/components/Categories/CategoryList";
import { Input } from "@/components/ui/input";
import * as React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SidebarProps {
  className?: string;
  children?: React.ReactNode;
}

interface SidebarContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(true);
  return (
    <SidebarContext.Provider value={{ isOpen, setIsOpen }}>
      <div className="flex w-full">{children}</div>
    </SidebarContext.Provider>
  );
}

export function useSidebarContext() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
}

export function SidebarTrigger() {
  const { isOpen, setIsOpen } = useSidebarContext();
  return (
    <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
      <svg 
        width="15" 
        height="15" 
        viewBox="0 0 15 15" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          d="M1.5 3C1.22386 3 1 3.22386 1 3.5C1 3.77614 1.22386 4 1.5 4H13.5C13.7761 4 14 3.77614 14 3.5C14 3.22386 13.7761 3 13.5 3H1.5ZM1 7.5C1 7.22386 1.22386 7 1.5 7H13.5C13.7761 7 14 7.22386 14 7.5C14 7.77614 13.7761 8 13.5 8H1.5C1.22386 8 1 7.77614 1 7.5ZM1 11.5C1 11.2239 1.22386 11 1.5 11H13.5C13.7761 11 14 11.2239 14 11.5C14 11.7761 13.7761 12 13.5 12H1.5C1.22386 12 1 11.7761 1 11.5Z" 
          fill="currentColor" 
          fillRule="evenodd" 
          clipRule="evenodd"
        />
      </svg>
    </Button>
  );
}

export function Sidebar({ className, children }: SidebarProps) {
  const { isOpen } = useSidebarContext();
  
  return (
    <div className={`${isOpen ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden h-full bg-background border-r flex-shrink-0 ${className}`}>
      {isOpen && (
        <div className="h-full flex flex-col">
          {children}
        </div>
      )}
    </div>
  );
}

export function SidebarHeader({ children }: { children: React.ReactNode }) {
  return <div className="px-3 py-2">{children}</div>;
}

export function SidebarContent({ children }: { children: React.ReactNode }) {
  return (
    <ScrollArea className="px-3 py-2 flex-1">
      {children}
    </ScrollArea>
  );
}

export function SidebarFooter({ children }: { children: React.ReactNode }) {
  return <div className="px-3 py-2 mt-auto">{children}</div>;
}

export function SidebarGroup({ children }: { children: React.ReactNode }) {
  return <div className="space-y-2">{children}</div>;
}

export function SidebarGroupLabel({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-2 px-2 text-sm font-medium text-muted-foreground">{children}</h3>;
}

export function SidebarGroupContent({ children }: { children: React.ReactNode }) {
  return <div className="space-y-1">{children}</div>;
}

export function SidebarMenu({ children }: { children: React.ReactNode }) {
  return <nav className="space-y-1">{children}</nav>;
}

export function SidebarMenuItem({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}

export function SidebarMenuButton({ 
  asChild, 
  children, 
  className, 
  onClick 
}: { 
  asChild?: boolean; 
  children: React.ReactNode; 
  className?: string; 
  onClick?: () => void;
}) {
  if (asChild) {
    return <div className={`w-full ${className || ''}`} onClick={onClick}>{children}</div>;
  }
  return <Button variant="ghost" className={`w-full justify-start ${className || ''}`} onClick={onClick}>{children}</Button>;
}

export function SidebarInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <Input {...props} />;
}
